import type {
  BrowserContext,
  BrowserFingerprint,
  BrowserThumbmark,
  EnvironmentContext,
  TrackingPrivacyMode
} from '../contracts';
import type { TrackerLogger } from '../logger';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { getThumbmark } from '@thumbmarkjs/thumbmarkjs';

// Optional comparison work can delay the first activity flush by at most one second.
const THUMBMARK_TIMEOUT_MS = 1_000;
// A source that never settles must not hold back every flush of the page.
const FINGERPRINT_TIMEOUT_MS = 2_000;

export const collectBrowserContext = (
  logger: TrackerLogger,
  privacyMode: TrackingPrivacyMode
): BrowserContext => {
  let timezone: string | null = null;
  if (privacyMode === 'persistent') {
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch (error) {
      logger.debug('collect_timezone', {
        outcome: 'unavailable',
        error: error instanceof Error ? error.message : String(error)
      });
      timezone = null;
    }
  }

  return {
    userAgent: navigator.userAgent,
    webdriver: Boolean(navigator.webdriver),
    languages: privacyMode === 'anonymous'
      ? [navigator.language?.split('-')[0] ?? ''].filter(Boolean)
      : Array.from(navigator.languages ?? []),
    timezone: privacyMode === 'anonymous' ? null : timezone
  };
};

const collectFingerprint = async (logger: TrackerLogger): Promise<BrowserFingerprint | null> => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const deadline = new Promise<null>((resolve) => {
      timeout = setTimeout(() => resolve(null), FINGERPRINT_TIMEOUT_MS);
    });
    const result = await Promise.race([
      deadline,
      FingerprintJS.load({ monitoring: false }).then(agent => agent.get())
    ]);
    if (!result) {
      logger.debug('collect_fingerprint', { outcome: 'unavailable', reason: 'timeout' });
      return null;
    }
    return {
      provider: 'fingerprintjs',
      visitorId: result.visitorId,
      version: result.version,
      confidence: result.confidence.score
    };
  } catch (error) {
    logger.debug('collect_fingerprint', {
      outcome: 'unavailable',
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
};

const collectThumbmark = async (logger: TrackerLogger): Promise<BrowserThumbmark | null> => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const deadline = new Promise<null>((resolve) => {
      timeout = setTimeout(() => resolve(null), THUMBMARK_TIMEOUT_MS);
    });
    const result = await Promise.race([
      deadline,
      getThumbmark({
        logging: false,
        collect_beacon: false,
        cache_lifetime_in_ms: 0,
        timeout: THUMBMARK_TIMEOUT_MS
      })
    ]);

    // Partial component results would make the comparison look less stable.
    if (!result || result.error?.length || !/^[a-f0-9]{32}$/.test(result.thumbmark) || !result.version) {
      logger.debug('collect_thumbmark', { outcome: 'unavailable' });
      return null;
    }
    return { hash: result.thumbmark, version: result.version };
  } catch (error) {
    logger.debug('collect_thumbmark', {
      outcome: 'unavailable',
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
};

export const collectEnvironmentContext = async (
  logger: TrackerLogger,
  privacyMode: TrackingPrivacyMode,
  browser: BrowserContext = collectBrowserContext(logger, privacyMode)
): Promise<EnvironmentContext> => {
  if (privacyMode === 'anonymous') return { browser };

  const [fingerprint, thumbmark] = await Promise.all([
    collectFingerprint(logger),
    collectThumbmark(logger)
  ]);

  return {
    browser,
    ...(fingerprint ? { fingerprint } : {}),
    ...(thumbmark ? { thumbmark } : {})
  };
};
